import { Component, h, Ref, useContext, useEffect, useMemo, useRef, useState } from "@lukekaalim/act";
import { a3 } from "@lukekaalim/act-three";

import * as three from 'three';
import { PhysicsDice } from "../../defs";
import { K3D, renderBeat, Snap } from "@kosmod/graphics-3d";
import { randomElement, useJeffect, useLogger } from "@kosmod/kit";
import { physicsBeat } from "../../hooks/usePhysics";
import { ObjectClone } from "../TerrainSystems/SimpleTerrainSystem";
import { TheatreState, TheatreStateContext, useTheatreState } from "../../hooks/useTheatreState";
import { TheatreCoreResources } from "../../hooks/useTheatreCoreResources";
import { SceneHTML } from "./Mini";
import { html } from "@lukekaalim/act-web";
import { TheatreSceneResources } from "../../hooks/useTheatreSceneResources";
import { PhysicsDiceID, TheatreData, TheatreMatrix4 } from "../..";

export type PhysicsDieRendererProps = {
  data: TheatreData,
  resources: TheatreSceneResources,
  die: PhysicsDice,

  simulation: Snap.Simulation,

  diceMap: Map<PhysicsDiceID, K3D.Transform>,
}

let count = 0;

export const PhysicsDieRenderer: Component<PhysicsDieRendererProps> = ({ die, simulation, resources, data, diceMap }) => {
  const ref = useRef<three.Mesh | null>(null);

  const logger = useLogger('PhysicsDieRenderer')

  const template = data.diceTemplates.get(die.dieTemplate);
  const meshTemplate = resources.dieMeshes.get(die.dieTemplate)
  const shape = resources.dieShapes.get(die.dieTemplate)

  if (!shape || !meshTemplate || !template)
    return null;

  const [result, setResult] = useState('');

  const subscribePhysics = physicsBeat.useBeat()
  useJeffect(j => {
    const previousTransform = data.remotePhysicsDieTransforms.get(die.id)
    
    const rotation = new three.Quaternion().random();
    const position = new three.Vector3();

    if (previousTransform) {
      position.copy(previousTransform.position);
      rotation.copy(previousTransform.rotation);
    } else {
      rotation.random();
      position.copy(die.initialTransform.position)
    }
    const myBody = simulation.body(shape, position, rotation);

    if (!previousTransform) {
      myBody.impulse({ x: 0, y: 100, z: 0 })
      myBody.aImpulse({ x: 15, y: 0, z: 15 })
    }

    //const diceShape = simulation.geometryShape(resources.cubeLo.geometry.clone().scale(.2, .2, .2));


    simulation.add(myBody);

    const mesh = ref.get()!;
    mesh.position.copy(myBody.position);

    const transform: K3D.Transform = {
      position: {
        x: mesh.position.x,
        y: mesh.position.y,
        z: mesh.position.z,
      },
      rotation: {
        x: mesh.quaternion.x,
        y: mesh.quaternion.y,
        z: mesh.quaternion.z,
        w: mesh.quaternion.w,
      },
    }
    diceMap.set(die.id, transform);

    j.add(subscribePhysics(() => {
      if (!myBody.active) {
        diceMap.delete(die.id)
        return;
      }
      diceMap.set(die.id, transform);

      mesh.position.copy(myBody.position);
      mesh.quaternion.copy(myBody.rotation);

      sceneHTMLPosition.copy(mesh.position)
        .add({ x: 0, y: .75, z: 0 })

      const up = new three.Vector3(0, 1, 0);
      
      transform.position.x = mesh.position.x;
      transform.position.y = mesh.position.y;
      transform.position.z = mesh.position.z;

      transform.rotation.x = mesh.quaternion.x;
      transform.rotation.y = mesh.quaternion.y;
      transform.rotation.z = mesh.quaternion.z;
      transform.rotation.w = mesh.quaternion.w;
      
      let closestFace = template.faces[0];
      let closestAngle = 100000;

      for (const face of template.faces) {
        const facePosition = new three.Vector3().copy(face.position).applyMatrix4(mesh.matrix);
        const angle = up.angleTo(facePosition)
        if (angle < closestAngle) {
          closestAngle = angle
          closestFace = face
        }
      }

      setResult(closestFace.value.toString())
    }))

    j.add(() => {
      myBody.remove();
      diceMap.delete(die.id);
    })
  }, [simulation]);

  const diceMesh = useMemo(() => {
    return h(a3.mesh, {
      geometry: meshTemplate.geometry,
      material: meshTemplate.material,
      ref,
      visible: true,
      scale: new three.Vector3(.2, .2, .2),
    })
  }, [])

  const state = useContext(TheatreStateContext)

  const sceneHTMLPosition = useRef(new three.Vector3()).get();

  return [
    diceMesh,
    h(SceneHTML, { position: sceneHTMLPosition, state }, [ 
      h(html.span, { style: { color: 'white', fontFamily: 'monospace', fontSize: '24px', fontWeight: 'bold' } }, result)
    ])
  ]
};

export type RemotePhysicsDieRendererProps = {
  res: TheatreSceneResources,
  data: TheatreData,
  
  die: PhysicsDice,
}

export const RemotePhysicsDie: Component<RemotePhysicsDieRendererProps> = ({ data, die, res }) => {
  const ref = useRef<three.Mesh | null>(null);

  const template = data.diceTemplates.get(die.dieTemplate);
  const meshTemplate = res.dieMeshes.get(die.dieTemplate);
  const transform = data.remotePhysicsDieTransforms.get(die.id);

  if (!template || !meshTemplate || !transform)
    return null;

  const logger = useLogger('RemotePhysicsDie');

  renderBeat.useBeat((now) => {
    const mesh = ref.get()!;
    
    mesh.position.copy(transform.position)
    mesh.quaternion.copy(transform.rotation);
  })

  return h(a3.mesh, {
    geometry: meshTemplate.geometry,
    material: meshTemplate.material,
    ref,
    scale: new three.Vector3(.2, .2, .2),
  })
};

const material = new three.MeshBasicMaterial({ color: 'blue', opacity: 0.5, transparent: true })